import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, MapPin, Phone, Star, Clock, Award, Zap, Camera, Bell, Home, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import DOMPurify from 'dompurify';

const AlarmeNarbonnePage = () => {
  const cities = [
    'Narbonne', 'Narbonne-Plage', 'Gruissan', 'Port-la-Nouvelle', 'Sigean',
    'Leucate', 'La Palme', 'Peyriac-de-Mer', 'Bages', 'Coursan',
    'Cuxac-d\'Aude', 'Lézignan-Corbières', 'Canet-d\'Aude', 'Ouveillan', 'Moussan',
    'Montredon-des-Corbières', 'Névian', 'Marcorignan', 'Villedaigne', 'Ornaisons',
    'Carcassonne', 'Trèbes', 'Limoux', 'Castelnaudary'
  ];

  const advantages = [
    { icon: Shield, title: "Expert Ajax Systems N°1", desc: "Installateur certifié et distributeur officiel Ajax Systems dans l'Aude" },
    { icon: Clock, title: "Intervention rapide", desc: "Installation sous 48h à Narbonne et dans tout le département 11" },
    { icon: Award, title: "Garantie 4 ans", desc: "Tous nos produits Ajax sont garantis 4 ans pièces et main d'œuvre" },
    { icon: Zap, title: "Autonomie exceptionnelle", desc: "Jusqu'à 7 ans d'autonomie sur batterie pour les détecteurs" },
  ];

  const services = [
    { icon: Bell, title: "Alarme sans fil Ajax", desc: "Systèmes d'alarme Grade 2 et Grade 3 pour particuliers et professionnels" },
    { icon: Camera, title: "Vidéosurveillance IA", desc: "Caméras avec détection intelligente humains/véhicules, zéro fausse alerte" },
    { icon: Home, title: "Domotique connectée", desc: "Automatisation éclairage, volets, chauffage via l'écosystème Ajax" },
    { icon: Building, title: "Sécurité entreprise", desc: "Solutions professionnelles pour vignobles, commerces et sites touristiques" },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "FGS Alarme Montpellier - Alarme Ajax Systems Narbonne",
    "description": "Installation alarme et vidéosurveillance Ajax Systems à Narbonne et dans l'Aude (11). Expert certifié, intervention rapide.",
    "url": "https://maprotection-ia.fr/alarme-ajax-narbonne",
    "telephone": "+33745040929",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "6 Bis Boulevard Berthelot",
      "addressLocality": "Montpellier",
      "postalCode": "34000",
      "addressRegion": "Occitanie",
      "addressCountry": "FR"
    },
    "areaServed": [
      { "@type": "City", "name": "Narbonne" },
      { "@type": "City", "name": "Carcassonne" },
      { "@type": "State", "name": "Aude" }
    ],
    "priceRange": "€€",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "45",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  return (
    <>
      <Helmet>
        <title>Alarme Narbonne : Installation Ajax Systems Aude (11) | Devis Gratuit | FGS Alarme</title>
        <meta name="description" content="★ Installateur alarme Narbonne certifié Ajax Systems. Alarme maison sans fil, vidéosurveillance IA, télésurveillance 24/7. Intervention Carcassonne, Gruissan sous 48h. Devis gratuit. ☎ 07 45 04 09 29" />
        <meta name="keywords" content="alarme narbonne, alarme ajax narbonne, installation alarme aude, vidéosurveillance narbonne, alarme sans fil 11, installateur ajax narbonne, alarme maison narbonne, sécurité carcassonne, alarme gruissan, prix alarme narbonne" />
        <link rel="canonical" href="https://maprotection-ia.fr/alarme-ajax-narbonne" />
        
        {/* Geo Meta Tags */}
        <meta name="geo.region" content="FR-11" />
        <meta name="geo.placename" content="Narbonne" />
        <meta name="geo.position" content="43.1833;3.0042" />
        <meta name="ICBM" content="43.1833, 3.0042" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Alarme Narbonne : Installation Ajax Systems Aude (11) | Devis Gratuit" />
        <meta property="og:description" content="Installateur certifié Ajax Systems à Narbonne. Alarme sans fil, vidéosurveillance IA, garantie 4 ans. Devis gratuit sous 24h." />
        <meta property="og:url" content="https://maprotection-ia.fr/alarme-ajax-narbonne" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:image" content="https://maprotection-ia.fr/og-image-fb.png" />
        
        {/* Schema.org */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(JSON.stringify(structuredData)) }} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://maprotection-ia.fr" },
              { "@type": "ListItem", "position": 2, "name": "Alarme Narbonne", "item": "https://maprotection-ia.fr/alarme-ajax-narbonne" }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-black/40">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-b from-fgs-surface to-fgs-bg overflow-hidden">
          <div className="container mx-auto px-4 relative z-10">
            <div className="flex items-center justify-center gap-2 mb-6">
              <MapPin className="w-5 h-5 text-emerald-400" />
              <span className="text-emerald-400 font-medium">Narbonne et département de l'Aude (11)</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center text-white mb-6">
              Installation <span className="text-emerald-400">Alarme Ajax Systems</span>
              <br />à Narbonne
            </h1>
            
            <p className="text-xl text-white/50 text-center max-w-3xl mx-auto mb-8">
              FGS Alarme Montpellier, votre expert en alarme et vidéosurveillance Ajax Systems 
              à Narbonne, Carcassonne et dans toute l'Aude. Installation professionnelle, garantie 4 ans.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-emerald-500 hover:bg-emerald-500/90 text-black font-semibold">
                <a href="tel:+33745040929">
                  <Phone className="w-5 h-5 mr-2" />
                  07 45 04 09 29
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-fgs-primary text-emerald-400 hover:bg-emerald-500/10">
                <Link to="/contact">Devis gratuit en 24h</Link>
              </Button>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap justify-center gap-6 mt-10">
              <div className="flex items-center gap-2 bg-white/[0.02]/50 px-4 py-2 rounded-full">
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <span className="text-white font-medium">4.9/5</span>
                <span className="text-white/50">(45 avis)</span>
              </div>
              <div className="flex items-center gap-2 bg-white/[0.02]/50 px-4 py-2 rounded-full">
                <Shield className="w-5 h-5 text-emerald-400" />
                <span className="text-white">Certifié Ajax</span>
              </div>
              <div className="flex items-center gap-2 bg-white/[0.02]/50 px-4 py-2 rounded-full">
                <Award className="w-5 h-5 text-emerald-400" />
                <span className="text-white">Garantie 4 ans</span>
              </div>
            </div>
          </div>
        </section>

        {/* Advantages Section */}
        <section className="py-16 bg-black/40">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-white mb-12">
              Pourquoi choisir FGS Alarme Montpellier à <span className="text-emerald-400">Narbonne</span> ?
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {advantages.map((adv, idx) => (
                <div key={idx} className="bg-white/[0.02] p-6 rounded-xl border border-white/5 hover:border-fgs-primary/50 transition-colors">
                  <adv.icon className="w-10 h-10 text-emerald-400 mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">{adv.title}</h3>
                  <p className="text-white/50 text-sm">{adv.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 bg-white/[0.02]/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-white mb-12">
              Nos services de sécurité dans l'<span className="text-emerald-400">Aude</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((svc, idx) => (
                <div key={idx} className="bg-black/40 p-6 rounded-xl border border-white/5">
                  <svc.icon className="w-10 h-10 text-emerald-400 mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">{svc.title}</h3>
                  <p className="text-white/50 text-sm">{svc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cities Section */}
        <section className="py-16 bg-black/40">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-white mb-4">
              Zone d'intervention dans l'<span className="text-emerald-400">Aude (11)</span>
            </h2>
            <p className="text-white/50 text-center mb-10 max-w-2xl mx-auto">
              Nous intervenons à Narbonne, Carcassonne et dans toutes les communes de l'Aude pour l'installation 
              de votre système d'alarme Ajax Systems.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {cities.map((city, idx) => (
                <span 
                  key={idx} 
                  className="px-3 py-1.5 bg-white/[0.02] border border-white/5 rounded-full text-sm text-white/50 hover:text-white hover:border-fgs-primary/50 transition-colors"
                >
                  {city}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-b from-fgs-surface/50 to-fgs-bg">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Protégez votre domicile à <span className="text-emerald-400">Narbonne</span>
            </h2>
            <p className="text-white/50 mb-8 max-w-2xl mx-auto">
              Recevez un devis gratuit sous 24h pour l'installation de votre alarme Ajax Systems 
              à Narbonne, Carcassonne ou dans l'Aude. Installation professionnelle garantie.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-emerald-500 hover:bg-emerald-500/90 text-black font-semibold">
                <a href="tel:+33745040929">
                  <Phone className="w-5 h-5 mr-2" />
                  Appeler maintenant
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-fgs-primary text-emerald-400 hover:bg-emerald-500/10">
                <Link to="/contact">Demander un devis</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AlarmeNarbonnePage;
